/**
 * Exam simulation state store.
 *
 * Zustand store for managing exam simulation selection flow state.
 * Handles exam type, level, and module selection with navigation between steps.
 *
 * @module useExamSimulationStore
 */

import { create } from 'zustand';
import { persist, type PersistStorage, type StorageValue } from 'zustand/middleware';
import { withDevTools } from './storeUtils';
import { generateSessionId } from '@/utils/sessionUtils';
import { logger } from '@/lib/logger';
import type {
  ExamType,
  CEFRLevel,
  ExamModule,
  ExamSimulationSelection,
  SelectionStep,
} from '@/types/exam-simulation';

/**
 * Exam simulation store state interface
 */
interface ExamSimulationState {
  /** Current selection state */
  selection: ExamSimulationSelection;
  /** Current step in the selection flow */
  currentStep: SelectionStep;
  /** Session ID for the current simulation (shared across all modules) */
  sessionId: string | null;
  /** In-memory-only grant for the currently authorized attempt. */
  attemptToken: string | null;

  // Actions
  /** Set selected exam type and advance to level selection */
  setExamType: (examType: ExamType) => void;
  /** Set selected level and advance to module selection */
  setLevel: (level: CEFRLevel) => void;
  /** Toggle module selection (ensures at least one remains selected) */
  toggleModule: (module: ExamModule) => void;
  /** Set modules directly (used for initialization from settings) */
  setModules: (modules: ExamModule[]) => void;
  /** Navigate to instructions screen */
  goToInstructions: () => void;
  /** Navigate to preparing screen */
  goToPreparing: () => void;
  /** Navigate to module introduction screen */
  goToModuleIntroduction: () => void;
  /** Navigate to module interface */
  goToModule: () => void;
  /** Navigate to finished screen */
  goToFinished: () => void;
  /** Navigate back to previous step */
  goBack: () => void;
  /** Reset selection to initial state */
  reset: () => void;
  /** Check if exam type is available for selected level */
  isExamTypeAvailable: (examType: ExamType) => boolean;
  /** Generate a new session ID for the simulation */
  generateSimulationSessionId: (userId: string) => void;
  /** Store the transient grant returned by the simulation-start endpoint. */
  setAttemptToken: (attemptToken: string | null) => void;
  /**
   * Restore selection, session, and step from a recovered draft.
   * Does NOT generate a new session id - the caller is responsible for
   * passing the same session id the draft was created under so that follow-up
   * submissions and draft keys line up.
   */
  rehydrateFromDraft: (input: {
    selection: ExamSimulationSelection;
    currentStep: SelectionStep;
    sessionId: string;
  }) => void;
}

/**
 * Exam simulation Zustand store.
 *
 * Manages the exam simulation selection flow state including:
 * - Exam type, level, and module selection
 * - Navigation between selection steps
 * - Step validation and availability checks
 *
 * @example
 * ```typescript
 * const { selection, currentStep, setExamType, setLevel } = useExamSimulationStore();
 * ```
 */
/**
 * Subset of the store persisted to localStorage so that a reload can restore
 * which step/session the learner was on. Module-level state (answers, notes,
 * essay text, timers) is NOT persisted here - it lives in the per-module
 * draft storage.
 */
interface PersistedSimulationState {
  readonly selection: ExamSimulationSelection;
  readonly currentStep: SelectionStep;
  readonly sessionId: string | null;
}

const STORAGE_KEY = 'dg:sim-store:v1';

/**
 * Safe localStorage adapter that degrades to a no-op in environments where
 * `localStorage` throws (privacy modes, SSR-like tests).
 */
const safeStorage: PersistStorage<PersistedSimulationState> = {
  getItem: (name) => {
    try {
      const raw = globalThis.localStorage.getItem(name);
      if (!raw) return null;
      return JSON.parse(raw) as StorageValue<PersistedSimulationState>;
    } catch (error) {
      logger.warn('Failed to read exam simulation store from localStorage', {
        error: String(error),
      });
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      globalThis.localStorage.setItem(name, JSON.stringify(value));
    } catch (error) {
      logger.warn('Failed to write exam simulation store to localStorage', {
        error: String(error),
      });
    }
  },
  removeItem: (name) => {
    try {
      globalThis.localStorage.removeItem(name);
    } catch {
      /* ignore */
    }
  },
};

export const useExamSimulationStore = create<ExamSimulationState>()(
  withDevTools(
    persist(
      (set, get) => ({
      // Initial state
      selection: {
        examType: null,
        level: null,
        modules: [],
      },
      currentStep: 'exam-type',
      sessionId: null,
      attemptToken: null,

      // Actions
      setExamType: (examType: ExamType) => {
        set((state) => ({
          selection: { ...state.selection, examType },
          currentStep: 'level',
        }));
      },

      setLevel: (level: CEFRLevel) => {
        set((state) => ({
          selection: { ...state.selection, level },
          currentStep: 'modules',
        }));
      },

      toggleModule: (module: ExamModule) => {
        set((state) => {
          const currentModules = state.selection.modules;
          const isSelected = currentModules.includes(module);

          if (isSelected) {
            // Prevent deselecting if it's the last module
            if (currentModules.length <= 1) {
              return state;
            }
            return {
              selection: {
                ...state.selection,
                modules: currentModules.filter((m) => m !== module),
              },
            };
          } else {
            return {
              selection: {
                ...state.selection,
                modules: [...currentModules, module],
              },
            };
          }
        });
      },

      setModules: (modules: ExamModule[]) => {
        set((state) => ({
          selection: { ...state.selection, modules },
        }));
      },

      goToInstructions: () => {
        set({ currentStep: 'instructions' });
      },

      goToPreparing: () => {
        set({ currentStep: 'preparing' });
      },

      goToModuleIntroduction: () => {
        set({ currentStep: 'module-intro' });
      },

      goToModule: () => {
        set({ currentStep: 'module' });
      },
      goToFinished: () => {
        set({ currentStep: 'finished' });
      },

      goBack: () => {
        const state = get();
        if (state.currentStep === 'module') {
          set({ currentStep: 'module-intro' });
        } else if (state.currentStep === 'module-intro') {
          set({ currentStep: 'preparing' });
        } else if (state.currentStep === 'preparing') {
          set({ currentStep: 'instructions' });
        } else if (state.currentStep === 'instructions') {
          set({ currentStep: 'modules' });
        } else if (state.currentStep === 'modules') {
          set({
            currentStep: 'level',
            selection: { ...state.selection, level: null, modules: [] },
          });
        } else if (state.currentStep === 'level') {
          set({
            currentStep: 'exam-type',
            selection: { ...state.selection, level: null, modules: [] },
          });
        }
      },

      reset: () => {
        set({
          selection: {
            examType: null,
            level: null,
            modules: [],
          },
          currentStep: 'exam-type',
          sessionId: null,
          attemptToken: null,
        });
      },

      generateSimulationSessionId: (userId: string) => {
        set({ sessionId: generateSessionId(userId) });
      },

      setAttemptToken: (attemptToken) => {
        set({ attemptToken });
      },

      rehydrateFromDraft: ({ selection, currentStep, sessionId }) => {
        set({ selection, currentStep, sessionId, attemptToken: null });
      },

      isExamTypeAvailable: (examType: ExamType): boolean => {
        const state = get();
        // ÖSD and telc are not available for A1 level
        if (state.selection.level === 'A1') {
          return examType === 'goethe';
        }
        return true;
      },
    }),
      {
        name: STORAGE_KEY,
        storage: safeStorage,
        // Persist only the recoverable flow metadata; everything else
        // (including the attempt token) is reconstructed on every page load.
        partialize: (state): PersistedSimulationState => ({
          selection: state.selection,
          currentStep: state.currentStep,
          sessionId: state.sessionId,
        }),
        version: 1,
      },
    ),
    'ExamSimulationStore',
  ),
);
